import { ArrowDown, SearchIcon } from "lucide-react";
import styles from "./BudgetTracker.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import { IBudgetTrackerJobDetails } from "~/utils/interface/jobDetails";
import { handleGetListBudgetTrackerJobDetails, handleToggleArchivedBudgetTracker } from "~/thunks/job-details/jobDetailsThunk";
import { formatMoney, notify, toDateOnly } from "~/utils/helper";
import dayjs from "dayjs";
import { ActionIcon } from "~/assets/icons";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import jobDetailsApi from "~/apis/jobDetails";
import FormToggle from "~/components/common/form/formToggle/FormToggle";

const cx = classNames.bind(styles);

interface BudgetTrackerProps {
  jobId: string;
  handleCreateBudgetTracker: (idBudget?: string, isEdit?: boolean) => void;
  handleViewDetailsBudget: (idBudget: string) => void;
  getListBudgetTracker: boolean;
  setGetListBudgetTracker: (value: boolean) => void;
  onAddNote?: () => void;
}

const BudgetTracker = ({
  jobId,
  handleCreateBudgetTracker,
  handleViewDetailsBudget,
  getListBudgetTracker,
  setGetListBudgetTracker,
  onAddNote,
}: BudgetTrackerProps) => {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { budgetTrackerJobDetailsList } = useAppSelector(
    (state) => state.jobDetails
  );
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const activeActionButtonRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const budgetTrackerTableRef = useRef<HTMLDivElement>(null);
  const [openModalAlertDelete, setOpenModalAlertDelete] = useState(false);
  const [budgetId, setBudgetId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPageOptions = [10, 20, 50, 100];

  useEffect(() => {
    if (jobId || getListBudgetTracker === true) {
      setIsLoading(true);
      dispatch(
        handleGetListBudgetTrackerJobDetails({
          idJob: jobId,
        })
      )
        .unwrap()
        .then((res) => {
          setIsLoading(false);
          setGetListBudgetTracker(false);
        })
        .catch((error: any) => {
          notify(
            "error",
            `Failed to fetch budget tracker list: ${error.response.data.message}`
          );
          setIsLoading(false);
          setGetListBudgetTracker(false);
        });
    }
  }, [dispatch, jobId, getListBudgetTracker, setGetListBudgetTracker]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (!showItemsDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showItemsDropdown]);

  useEffect(() => {
    const handleScroll = () => {
      if (activeMenu) {
        setActiveMenu(null);
        activeActionButtonRef.current = null;
      }
    };

    const handleTableScroll = () => {
      if (activeMenu) {
        setActiveMenu(null);
        activeActionButtonRef.current = null;
      }
    };

    const handleResize = () => {
      if (activeMenu && activeActionButtonRef.current) {
        requestAnimationFrame(() => {
          const position = calculateActionMenuPosition(
            activeActionButtonRef.current!
          );
          setMenuPosition(position);
        });
      }
    };

    const tableElement = budgetTrackerTableRef.current;

    window.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    document.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", handleResize);

    if (tableElement) {
      tableElement.addEventListener("scroll", handleTableScroll, {
        passive: true,
      });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll, {
        capture: true,
      } as EventListenerOptions);
      document.removeEventListener("scroll", handleScroll, {
        capture: true,
      } as EventListenerOptions);
      window.removeEventListener("resize", handleResize);
      if (tableElement) {
        tableElement.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [activeMenu]);

  const filteredBudgetTrackers = useMemo(() => {
    let filtered = budgetTrackerJobDetailsList?.responses || [];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((budgetTracker: IBudgetTrackerJobDetails) => {
        const budgetNumber = budgetTracker.budgetNumber?.toLowerCase() || "";
        const budgetDate = budgetTracker.budgetDate
          ? dayjs(toDateOnly(budgetTracker.budgetDate)).format("MM/DD/YYYY")
          : "";
        const grossProfit = budgetTracker.gross?.toLowerCase() || "";
        const netProfit = budgetTracker.net?.toLowerCase() || "";

        return (
          budgetNumber.includes(query) ||
          budgetDate.includes(query) ||
          grossProfit.includes(query) ||
          netProfit.includes(query) ||
          (budgetTracker.saleRepresentativeName?.toLowerCase() || "").includes(query)
        );
      });
    }

    return filtered;
  }, [budgetTrackerJobDetailsList?.responses, searchQuery]);

  const totalItems = filteredBudgetTrackers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handleChangePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPage(1);
    setShowItemsDropdown(false);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }

    return pages.filter((p, idx, arr) => !(p === arr[idx - 1]));
  };

  const paginatedBudgets = useMemo(() => {
    return filteredBudgetTrackers.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  }, [filteredBudgetTrackers, page, itemsPerPage]);

  const showingCount = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const remaining = Math.max(totalItems - startIndex, 0);
    return Math.min(itemsPerPage, remaining);
  }, [page, itemsPerPage, totalItems]);

  const calculateActionMenuPosition = (buttonElement: HTMLDivElement) => {
    const rect = buttonElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const menuWidth = 230;
    const menuHeight = 200;

    const spacing = 5;
    const padding = 8;

    const spaceBelow = viewportHeight - rect.bottom - spacing - padding;
    const spaceAbove = rect.top - spacing - padding;

    let top: number;
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      top = rect.top - menuHeight - spacing;
      if (top < padding) {
        top = padding;
      }
    } else {
      top = rect.bottom + spacing;
      if (top + menuHeight > viewportHeight - padding) {
        if (spaceAbove > menuHeight) {
          top = rect.top - menuHeight - spacing;
        } else {
          top = Math.max(padding, viewportHeight - menuHeight - padding);
        }
      }
    }

    let left = rect.left - menuWidth + rect.width;

    if (left + menuWidth > viewportWidth - padding) {
      left = viewportWidth - menuWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }

    return {
      top,
      left,
    };
  };

  const handleActionClick = (
    event: React.MouseEvent,
    budget: IBudgetTrackerJobDetails
  ) => {
    event.stopPropagation();

    if (activeMenu === budget.id) {
      setActiveMenu(null);
      activeActionButtonRef.current = null;
      return;
    }

    const buttonElement = event.currentTarget as HTMLDivElement;
    activeActionButtonRef.current = buttonElement;
    const position = calculateActionMenuPosition(buttonElement);
    setMenuPosition(position);
    setActiveMenu(budget.id);
  };

  const ActionMenu = ({ budget, onClose, position }: any) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleEdit = () => {
      onClose();
      handleCreateBudgetTracker(budget.id, true);
    };

    const handleView = () => {
      onClose();
      handleViewDetailsBudget(budget.id);
    };

    const handleDelete = () => {
      setOpenModalAlertDelete(true);
      setBudgetId(budget.id);
      onClose();
    };

    const handleAddNote = () => {
      onClose();
      if (onAddNote) {
        onAddNote();
      }
    };

    return (
      <div
        ref={menuRef}
        className={cx("action-menu")}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        <button className={cx("menu-item")} onClick={handleEdit}>
          Edit
        </button>

        <button className={cx("menu-item")} onClick={handleView}>
          View
        </button>

        <button className={cx("menu-item")} onClick={handleDelete}>
          Delete
        </button>

        <button className={cx("menu-item")} onClick={handleAddNote}>
          Add Note
        </button>
      </div>
    );
  };

  const handleDeleteBudgetTracker = async () => {
    try {
      setIsLoading(true);
      await jobDetailsApi.deleteBudgetTracker(jobId, budgetId || "");
      setIsLoading(false);
      notify("success", "Delete budget tracker successfully");
      setOpenModalAlertDelete(false);
      setBudgetId(null);
      setGetListBudgetTracker(true);
    } catch (error: any) {
      notify("error", error.response?.data?.message || "Failed to delete budget tracker");
      setIsLoading(false);
    }
  };

  const handleToggleArchived = async (id: string) => {
    try {
      await dispatch(
        handleToggleArchivedBudgetTracker({ idJob: jobId, idBudget: id })
      ).unwrap();
      notify("success", "Budget tracker archived status updated");
    } catch (error: any) {
      notify(
        "error",
        error?.response?.data?.message || "Failed to update archived status"
      );
    }
  };

  const formatMoney = (value?: string | number | null) => {
    if (value === null || value === undefined || value === "") return "-";
    const amount = Number(value);
    if (Number.isNaN(amount)) return "-";
    return `$${amount.toFixed(2)}`;
  };

  return (
    <div className={cx("budget-tracker-container")}>
      <div className={cx("budget-tracker-header")}>
        <div className={cx("search-container")}>
          <SearchIcon className={cx("search-icon")} />
          <input
            type="text"
            placeholder="Search Budgets"
            className={cx("search-input")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <PrimaryButton
          title="Create Budget"
          className={cx("create-button")}
          onClick={() => handleCreateBudgetTracker()}
        />
      </div>

      <div ref={budgetTrackerTableRef} className={cx("budget-tracker-table")}>
        {isLoading ? (
          <div className={cx("loading-overlay")}>
            <LoadingMainContent />
          </div>
        ) : (
          <>
            <div className={cx("table-wrapper")}>
              <table className={cx("sections-table")}>
                <thead>
                  <tr>
                    <th className={cx("col-budget-number")}>Budget #</th>
                    <th className={cx("col-budget-date")}>Budget Date</th>
                    <th className={cx("col-material")}>Material Cost</th>
                    <th className={cx("col-labor")}>Labor Cost</th>
                    <th className={cx("col-expenses")}>Expenses</th>
                    <th className={cx("col-sales-rep")}>Sales Representative</th>
                    <th className={cx("col-commission")}>Commission</th>
                    <th className={cx("col-gross")}>Gross Profit</th>
                    <th className={cx("col-net")}>Net Profit</th>
                    <th className={cx("col-is-archived")}>Is Archived</th>
                    <th className={cx("col-actions")}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBudgets.map((budget: IBudgetTrackerJobDetails) => (
                    <tr key={budget.id} className={cx("item-row")}>
                      <td className={cx("col-budget-number")}>
                        <div
                          className={cx("cell")}
                          onClick={() => handleViewDetailsBudget(budget.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <p className={cx("text-content", "budget-number")}>
                            {budget.budgetNumber || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-budget-date")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {budget.budgetDate
                              ? dayjs(toDateOnly(budget.budgetDate)).format("MM/DD/YYYY")
                              : "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-material")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {formatMoney(budget.materialCost)}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-labor")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {formatMoney(budget.laborCost)}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-expenses")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {formatMoney(budget.expenseCost)}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-sales-rep")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {budget.saleRepresentativeName || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-commission")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {formatMoney(budget.commission)}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-gross")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {formatMoney(budget.gross)}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-net")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {formatMoney(budget.net)}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-is-archived")}>
                        <div className={cx("cell")}>
                          <FormToggle
                            inputkey={"gutter"}
                            value={budget.isArchived}
                            onChange={() => {
                              handleToggleArchived(budget.id);
                            }}
                            text={""}
                          />
                        </div>
                      </td>
                      <td className={cx("col-actions")}>
                        <div
                          className={cx("action-icon-container")}
                          onClick={(e) => handleActionClick(e, budget)}
                        >
                          <div>
                            <ActionIcon className={cx("action-icon")} />
                          </div>
                          {activeMenu === budget.id && (
                            <ActionMenu
                              budget={budget}
                              onClose={() => {
                                setActiveMenu(null);
                                activeActionButtonRef.current = null;
                              }}
                              position={menuPosition}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={cx("pagination-container")}>
              <div className={cx("pagination-left")}>
                <span className={cx("pagination-text")}>Show result</span>
                <div className={cx("items-per-page-wrapper")} ref={dropdownRef}>
                  <button
                    className={cx("items-per-page-button")}
                    onClick={() => setShowItemsDropdown(!showItemsDropdown)}
                  >
                    <span>{showingCount}</span>
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 9 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z"
                        fill="#789EB4"
                      />
                    </svg>
                  </button>
                  {showItemsDropdown && (
                    <div className={cx("items-per-page-dropdown")}>
                      {itemsPerPageOptions.map((option) => (
                        <button
                          key={option}
                          className={cx("dropdown-item", {
                            active: itemsPerPage === option,
                          })}
                          onClick={() => handleItemsPerPageChange(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className={cx("pagination-text")}>out of</span>
                <span className={cx("pagination-total")}>{totalItems}</span>
              </div>

              <div className={cx("pagination-right")}>
                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === 1 || page === 0,
                  })}
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 1 || page === 0}
                >
                  <ArrowDown className={cx("nav-icon", "rotate-90")} />
                  <span>Previous</span>
                </button>

                <div className={cx("page-numbers")}>
                  {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className={cx("page-ellipsis")}
                        >
                          ...
                        </span>
                      );
                    }
                    const pageNumber = Number(pageNum);
                    const isActive = page === pageNumber;
                    return (
                      <button
                        key={pageNumber}
                        className={cx("page-number", {
                          active: isActive,
                        })}
                        onClick={() => handleChangePage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === totalPages,
                  })}
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  <span>Next</span>
                  <ArrowDown className={cx("nav-icon", "!rotate-[270deg]")} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ModalAlertDelete
        open={openModalAlertDelete}
        description="Are you sure you want to delete this budget tracker?"
        onCancel={() => setOpenModalAlertDelete(false)}
        onConfirm={handleDeleteBudgetTracker}
      />
    </div>
  );
};

export default BudgetTracker;
